import { useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { buildWaLink } from '@/config/brand';
import { useContent } from '@/lib/content';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { trackEvent } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ['Wedding', 'Birthday', 'Baby Shower', 'Cradle Ceremony', 'Devotional Event', 'Corporate Event', 'School / Community Event', 'Other'];
const BUDGETS = ['Under ₹1 Lakh', '₹1 – 3 Lakhs', '₹3 – 5 Lakhs', '₹5 – 10 Lakhs', '₹10 Lakhs+', 'Not sure yet'];

const initial = { name: '', phone: '', email: '', event_type: '', event_date: '', guest_count: '', location: '', budget: '', requirements: '', message: '' };

export const Enquiry = () => {
  const { brand } = useContent();
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || form.phone.replace(/\D/g, '').length < 10) {
      setError('Please share your name and a valid 10-digit phone number.');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, form);
      trackEvent('form_submit', { event_type: form.event_type });
      const msg = `Hi Events by AJ, I am ${form.name}. I am planning a ${form.event_type || 'an event'}${form.event_date ? ` on ${form.event_date}` : ''}${form.location ? ` in ${form.location}` : ''}. ${form.message || 'I would like to know more about your services.'}`;
      window.open(buildWaLink(brand.whatsapp, msg), '_blank');
      setDone(data);
    } catch (err) {
      setError('Something went wrong. Please try again or reach us on WhatsApp.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="py-24 sm:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-24">
        <div>
          <SectionHeading chapter="09" label="Enquire" title={<>Tell us about <span className="italic text-champagne">your event</span></>} />
          <Reveal delay={0.15} className="mt-8">
            <p className="text-white/60 font-light leading-relaxed text-base md:text-lg max-w-md">
              Share a few details and we&rsquo;ll get back with ideas, availability and a free quote —
              usually within a few hours.
            </p>
            <div className="mt-10 space-y-5">
              {['Free consultation & quote', 'Response within hours, not days', 'Serving Hyderabad & all of Telangana'].map((point) => (
                <div key={point} className="flex items-center gap-4">
                  <span className="w-1.5 h-1.5 bg-champagne rotate-45" />
                  <p className="text-white/70 font-light tracking-wide">{point}</p>
                </div>
              ))}
            </div>
            <a data-testid="enquiry-whatsapp-link" href={buildWaLink(brand.whatsapp)} target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent('whatsapp_click', { location: 'enquiry' })}
              className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-champagne hover:text-cream transition-colors">
              <FaWhatsapp size={18} /> Prefer WhatsApp? Chat with us directly
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          {done ? (
            <div data-testid="enquiry-success" className="h-full min-h-[420px] border border-champagne/40 bg-surface p-10 flex flex-col items-start justify-center">
              <CheckCircle2 size={44} className="text-champagne" />
              <h3 className="mt-6 font-serif text-3xl font-light text-cream">Thank you, {done.name.split(' ')[0]}.</h3>
              <p className="mt-4 text-white/60 font-light leading-relaxed">
                Your enquiry has been received and WhatsApp has been opened with your details.
                Our team will reach out shortly to plan something unforgettable.
              </p>
              <p className="mt-6 text-[11px] uppercase tracking-[0.3em] text-white/40">Reference: {done.id.slice(0, 8).toUpperCase()}</p>
              <button data-testid="enquiry-another-btn" onClick={() => { setDone(null); setForm(initial); }} className="btn-outline mt-8">
                Send Another Enquiry
              </button>
            </div>
          ) : (
            <form data-testid="enquiry-form" onSubmit={submit} className="border border-white/10 bg-surface p-7 sm:p-10">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                <div>
                  <label htmlFor="enq-name" className="caption-label block mb-1">Name *</label>
                  <input id="enq-name" data-testid="enquiry-name" className="input-lux" placeholder="Your full name" value={form.name} onChange={set('name')} required />
                </div>
                <div>
                  <label htmlFor="enq-phone" className="caption-label block mb-1">Phone Number *</label>
                  <input id="enq-phone" data-testid="enquiry-phone" type="tel" className="input-lux" placeholder="+91 ..." value={form.phone} onChange={set('phone')} required />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enq-email" className="caption-label block mb-1">Email</label>
                  <input id="enq-email" data-testid="enquiry-email" type="email" className="input-lux" placeholder="you@example.com" value={form.email} onChange={set('email')} />
                </div>
                <div>
                  <label htmlFor="enq-type" className="caption-label block mb-1">Event Type</label>
                  <select id="enq-type" data-testid="enquiry-event-type" className="input-lux appearance-none cursor-pointer" value={form.event_type} onChange={set('event_type')}>
                    <option value="" className="bg-ink">Select event type</option>
                    {EVENT_TYPES.map((t) => <option key={t} value={t} className="bg-ink">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="enq-date" className="caption-label block mb-1">Event Date</label>
                  <input id="enq-date" data-testid="enquiry-date" type="date" className="input-lux [color-scheme:dark]" value={form.event_date} onChange={set('event_date')} />
                </div>
                <div>
                  <label htmlFor="enq-guests" className="caption-label block mb-1">Expected Guests</label>
                  <input id="enq-guests" data-testid="enquiry-guests" className="input-lux" placeholder="e.g. 200" value={form.guest_count} onChange={set('guest_count')} />
                </div>
                <div>
                  <label htmlFor="enq-location" className="caption-label block mb-1">Event Location</label>
                  <input id="enq-location" data-testid="enquiry-location" className="input-lux" placeholder="Hyderabad" value={form.location} onChange={set('location')} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enq-budget" className="caption-label block mb-1">Approximate Budget</label>
                  <select id="enq-budget" data-testid="enquiry-budget" className="input-lux appearance-none cursor-pointer" value={form.budget} onChange={set('budget')}>
                    <option value="" className="bg-ink">Select a range</option>
                    {BUDGETS.map((b) => <option key={b} value={b} className="bg-ink">{b}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enq-requirements" className="caption-label block mb-1">Requirements</label>
                  <input id="enq-requirements" data-testid="enquiry-requirements" className="input-lux" placeholder="Decor, photography, catering, anchoring..." value={form.requirements} onChange={set('requirements')} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enq-message" className="caption-label block mb-1">Message</label>
                  <textarea id="enq-message" data-testid="enquiry-message" rows={3} className="input-lux resize-none" placeholder="Tell us about your celebration..." value={form.message} onChange={set('message')} />
                </div>
              </div>

              {error && <p data-testid="enquiry-error" className="mt-6 text-sm text-red-400">{error}</p>}

              <button data-testid="enquiry-submit-btn" type="submit" disabled={sending} className="btn-gold w-full mt-9 disabled:opacity-60">
                {sending ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : 'Get Your Free Quote'}
              </button>
              <p className="mt-4 text-center text-[11px] text-white/30 tracking-wide">
                By submitting, your details open in WhatsApp too — no spam, ever. Call us: {brand.phoneDisplay}
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
